// Exports: useTaskApi
import { useCallback } from "react";
import { taskApi } from "api/tasks/taskApi";
import { useAlert } from "components/UI_components/Alert";
import {
  normalizeGroup,
  normalizeSubtask,
  normalizeTask,
} from "utils/tasks/taskNormalizers";
import { toArray } from "utils/tasks/taskHelpers";

const hasPermissionDetail = (response) =>
  Boolean(response?.detail || response?.data?.detail);

const useTaskApi = () => {
  const { addAlert } = useAlert();

  const runRequest = useCallback(
    async (request, options = {}) => {
      const {
        errorMessage = "Task request failed. Please try again.",
        logLabel = "Task request failed",
        shouldAlert = true,
      } = options;

      try {
        return await request();
      } catch (error) {
        console.error(logLabel, error);
        if (shouldAlert && errorMessage) {
          addAlert(errorMessage, "error");
        }
        throw error;
      }
    },
    [addAlert]
  );

  const getSiteMembers = useCallback(
    (siteId, options = {}) =>
      runRequest(() => taskApi.getSiteMembers(siteId), {
        errorMessage: "Failed to load site members.",
        logLabel: "Fetching site members failed",
        ...options,
      }),
    [runRequest]
  );

  const getTaskMembers = useCallback(
    (taskId, options = {}) =>
      runRequest(() => taskApi.getTaskMembers(taskId), {
        errorMessage: "Failed to load task members.",
        logLabel: "Fetching task members failed",
        ...options,
      }),
    [runRequest]
  );

  const addTaskMembers = useCallback(
    (payload, options = {}) =>
      runRequest(() => taskApi.addTaskMembers(payload), {
        errorMessage: "Failed to add task members.",
        logLabel: "Adding task members failed",
        ...options,
      }),
    [runRequest]
  );

  const updateTaskGroup = useCallback(
    (groupId, payload, options = {}) =>
      runRequest(() => taskApi.updateTaskGroup(groupId, payload), {
        errorMessage: "Failed to update task group.",
        logLabel: "Updating task group failed",
        ...options,
      }),
    [runRequest]
  );

  const addTaskGroup = useCallback(
    (payload, options = {}) =>
      runRequest(() => taskApi.addTaskGroup(payload), {
        errorMessage: "Could not create task group.",
        logLabel: "Creating task group failed",
        ...options,
      }),
    [runRequest]
  );

  const addTask = useCallback(
    (payload, options = {}) =>
      runRequest(() => taskApi.addTask(payload), {
        errorMessage: "Could not create task.",
        logLabel: "Creating task failed",
        ...options,
      }),
    [runRequest]
  );

  const addSubtask = useCallback(
    (payload, options = {}) =>
      runRequest(() => taskApi.addSubtask(payload), {
        errorMessage: "Could not create subtask.",
        logLabel: "Creating subtask failed",
        ...options,
      }),
    [runRequest]
  );

  const updateTask = useCallback(
    (taskId, payload, options = {}) =>
      runRequest(() => taskApi.updateTask(taskId, payload), {
        errorMessage: "Failed to update task. Please try again.",
        logLabel: "Updating task failed",
        ...options,
      }),
    [runRequest]
  );

  const updateSubtask = useCallback(
    (subtaskId, payload, options = {}) =>
      runRequest(() => taskApi.updateSubtask(subtaskId, payload), {
        errorMessage: "Failed to update subtask. Please try again.",
        logLabel: "Updating subtask failed",
        ...options,
      }),
    [runRequest]
  );

  const deleteTask = useCallback(
    (taskId, options = {}) =>
      runRequest(() => taskApi.deleteTask(taskId), {
        errorMessage: "Failed to delete task.",
        logLabel: "Deleting task failed",
        ...options,
      }),
    [runRequest]
  );

  const deleteSubtask = useCallback(
    (subtaskId, options = {}) =>
      runRequest(() => taskApi.deleteSubtask(subtaskId), {
        errorMessage: "Failed to delete subtask. Please try again.",
        logLabel: "Deleting subtask failed",
        ...options,
      }),
    [runRequest]
  );

  const fetchTasksForGroup = useCallback(
    async (groupId, expandedTasks = {}, options = {}) => {
      const tasks = toArray(
        await runRequest(() => taskApi.getAllTasks(groupId), {
          errorMessage: "Couldn't load tasks.",
          logLabel: `Fetching tasks for group ${groupId} failed`,
          ...options,
        })
      );

      return tasks.map((task) => normalizeTask(task, groupId, expandedTasks));
    },
    [runRequest]
  );

  const fetchSubtasksForTask = useCallback(
    async (groupId, task, options = {}) => {
      const taskId = typeof task === "object" ? task?.id : task;
      const taskStatus =
        typeof task === "object" ? task?.status : options.taskStatus;

      const subtasks = toArray(
        await runRequest(() => taskApi.getAllSubtasks(taskId), {
          errorMessage: "Couldn't load subtasks.",
          logLabel: `Fetching subtasks for task ${taskId} failed`,
          ...options,
        })
      );

      return subtasks.map((subtask) =>
        normalizeSubtask(subtask, groupId, {
          id: taskId,
          status: taskStatus,
        })
      );
    },
    [runRequest]
  );

  const statePersistentRefresh = useCallback(
    async (siteId, expandedGroups = {}, expandedTasks = {}, options = {}) => {
      const rawGroups = toArray(
        await runRequest(() => taskApi.getAllTaskGroupsBySite(siteId), {
          errorMessage: "Failed to refresh task groups.",
          logLabel: "Refreshing task groups failed",
          ...options,
        })
      );

      const groups = rawGroups
        .map(normalizeGroup)
        .filter(Boolean)
        .map((group) => ({
          ...group,
          expanded: Boolean(expandedGroups[group.id]),
          tasks: [],
          tasksFetched: false,
        }));

      await Promise.all(
        groups
          .filter((group) => group.expanded)
          .map(async (group) => {
            try {
              const tasks = await fetchTasksForGroup(
                group.id,
                expandedTasks,
                {
                  shouldAlert: false,
                }
              );

              group.tasks = tasks;
              group.tasksFetched = true;

              await Promise.all(
                tasks
                  .filter((task) => task.expanded)
                  .map(async (task) => {
                    try {
                      task.subtasks = await fetchSubtasksForTask(
                        group.id,
                        task,
                        {
                          shouldAlert: false,
                        }
                      );
                    } catch (error) {
                      console.warn(
                        `Failed to refresh subtasks for task ${task.id}`,
                        error
                      );
                    }
                  })
              );
            } catch (error) {
              console.warn(
                `Failed to refresh expanded group ${group.id}`,
                error
              );
            }
          })
      );

      return groups;
    },
    [fetchSubtasksForTask, fetchTasksForGroup, runRequest]
  );

  return {
    addTaskGroup,
    addTask,
    addSubtask,
    updateTaskGroup,
    updateTask,
    updateSubtask,
    deleteTask,
    deleteSubtask,
    fetchTasksForGroup,
    fetchSubtasksForTask,
    statePersistentRefresh,
    getSiteMembers,
    getTaskMembers,
    addTaskMembers,
    hasPermissionDetail,
  };
};

export default useTaskApi;
